import { DandyLinearTeethSVGs, getDandyLinearInterproximalSpaceSVG } from './DandyLinearLayoutSVGs';
import { AllInterproximalSpaces, AllToothNumbers } from '@orthly/items';
import type { InterproximalSpace, ToothNumber } from '@orthly/items';
import { stylesFactory, FlossPalette, useBrowserIsSafari } from '@orthly/ui-primitives';
import cx from 'classnames';
import React from 'react';

const useStyles = stylesFactory(() => ({
    horizontalSvg: {
        width: '100%',
    },
    verticalSvg: {
        height: 300,
        transformOrigin: 'center',
        transform: 'translate(-300px, 330px) rotate(90deg)',
    },
    verticalElement: {
        transformBox: 'fill-box',
        transformOrigin: 'center',
        transform: 'rotate(-90deg)',
    },
    flipElement: {
        transformBox: 'fill-box',
        transformOrigin: 'center',
        transform: 'rotate(180deg)',
    },
}));

interface LinearToothProps {
    number: ToothNumber;
    verticalLayout: boolean;
}

const LinearTooth: React.FC<LinearToothProps> = ({ number, verticalLayout }) => {
    const classes = useStyles();
    const { Tooth } = DandyLinearTeethSVGs[number];

    return <Tooth className={cx({ [classes.verticalElement]: verticalLayout })} />;
};

interface LinearInterproximalSpaceActionComponentProps {
    space: InterproximalSpace;
}

interface LinearInterproximalSpaceProps {
    space: InterproximalSpace;
    hasItem: boolean;
    verticalLayout: boolean;
    activeColor?: string;
    Action?: React.ComponentType<LinearInterproximalSpaceActionComponentProps>;
}

const LinearInterproximalSpace: React.FC<LinearInterproximalSpaceProps> = ({
    space,
    hasItem,
    activeColor,
    verticalLayout,
    Action,
}) => {
    const classes = useStyles();
    const isSafari = useBrowserIsSafari();
    const { Line, ActionWrapper } = getDandyLinearInterproximalSpaceSVG(space);

    return (
        <>
            <Line
                {...(hasItem ? { stroke: activeColor ?? FlossPalette.BURGUNDY } : {})}
                {...(hasItem ? { strokeWidth: 2 } : {})}
            />
            {Action && (
                <ActionWrapper className={verticalLayout && !isSafari ? classes.verticalElement : undefined}>
                    <Action space={space} />
                </ActionWrapper>
            )}
        </>
    );
};

interface InterproximalSpaceItem {
    space: InterproximalSpace;
    activeColor?: string;
}

interface DandyMouthLinearDisplayProps {
    interproximalSpaceItems?: InterproximalSpaceItem[];
    verticalLayout?: boolean;
    InterproximalSpaceAction?: React.ComponentType<LinearInterproximalSpaceActionComponentProps>;
}

export const DandyMouthLinearDisplay: React.FC<DandyMouthLinearDisplayProps> = ({
    interproximalSpaceItems = [],
    verticalLayout = false,
    InterproximalSpaceAction,
}) => {
    const classes = useStyles();

    return (
        <svg
            viewBox={'0 0 712 227'}
            fill={'none'}
            xmlns={'http://www.w3.org/2000/svg'}
            className={verticalLayout ? classes.verticalSvg : classes.horizontalSvg}
        >
            <rect width={712} height={227} rx={16} fill={'#FDFCFA'} />
            <path stroke={'#7E7C78'} d={'M40 114.976h632'} />
            <path
                d={
                    'M17.552 132.557L17.552 129.933C17.552 128.493 18.048 127.853 18.96 127.853C19.904 127.853 20.368 128.541 20.368 129.981L20.368 132.557L17.552 132.557ZM26 125.421L25.888 125.421C25.552 125.853 25.024 126.173 23.424 126.445C22.24 126.653 21.376 127.341 21.168 128.445C21.088 127.037 20.112 125.853 18.816 125.853C17.056 125.853 15.92 127.085 15.92 129.645L15.92 134.541L26 134.541L26 132.557L21.92 132.557L21.92 130.077C21.92 128.893 22.64 128.413 24.016 128.301C24.976 128.205 25.744 128.045 26 127.773L26 125.421ZM17.68 123.858L17.68 121.762L15.92 121.762L15.92 123.858L17.68 123.858ZM18.944 123.762L26 123.762L26 121.858L18.944 121.858L18.944 123.762ZM21.12 115.223C21.904 115.223 22.384 115.767 22.384 116.503C22.384 117.239 21.904 117.783 21.12 117.783C20.352 117.783 19.856 117.239 19.856 116.503C19.856 115.767 20.352 115.223 21.12 115.223ZM26.32 112.183C25.168 112.183 24.384 113.079 24.384 114.615L24.384 117.607C24.384 118.183 24.16 118.407 23.856 118.407C23.552 118.407 23.376 118.199 23.264 117.959C23.392 117.527 23.472 117.047 23.472 116.519C23.472 114.663 22.576 113.335 21.12 113.335C20.688 113.335 20.304 113.463 19.984 113.671C19.664 113.287 19.456 112.519 19.6 111.911L18.096 112.055C17.952 113.191 18.64 113.991 19.328 114.359C18.96 114.919 18.752 115.687 18.752 116.519C18.752 118.375 19.664 119.671 21.12 119.671C21.888 119.671 22.512 119.303 22.912 118.695C23.168 119.415 23.664 119.895 24.336 119.895C24.928 119.895 25.44 119.511 25.632 118.839C25.728 119.703 26.272 120.311 26.96 120.311C28.304 120.311 28.816 118.551 28.816 116.519C28.816 114.343 28.064 112.183 26.32 112.183ZM26.768 118.631C26.256 118.631 25.984 118.183 25.984 117.623L25.984 115.127C25.984 114.471 26.176 114.007 26.688 114.007C27.424 114.007 27.776 115.095 27.776 116.503C27.776 117.687 27.504 118.631 26.768 118.631ZM15.92 108.812L15.92 110.716L26 110.716L26 108.812L22.352 108.812C21.12 108.812 20.272 108.092 20.272 107.052C20.272 106.076 20.896 105.548 21.984 105.548L26 105.548L26 103.644L21.6 103.644C20.032 103.644 18.752 104.428 18.752 106.316C18.752 107.468 19.424 108.46 20.272 108.812L15.92 108.812ZM18.944 102.25L20.304 102.25L20.304 100.874L23.6 100.874C25.376 100.874 26.192 99.6899 26.192 98.2339C26.192 96.9219 25.456 95.9939 24.688 95.5619L23.504 96.5059C24.24 96.9059 24.64 97.1939 24.64 97.9459C24.64 98.5699 24.224 99.0179 23.44 99.0179L20.304 99.0179L20.304 96.2499L18.944 96.2499L18.944 99.0179L16.688 99.0179L17.12 100.874L18.944 100.874L18.944 102.25Z'
                }
                fill={'#7E7C78'}
            />
            <path
                d={
                    'M696.08 101.412L686 101.412L686 109.38L687.76 109.38L687.76 103.428L696.08 103.428L696.08 101.412ZM688.4 116.361C687.616 116.025 687.232 115.433 687.232 114.489C687.232 113.353 688.032 112.521 689.248 112.505L689.248 117.865L689.76 117.865C691.616 117.865 693.248 116.697 693.248 114.377C693.248 112.233 691.648 110.617 689.424 110.617C687.264 110.617 685.808 112.201 685.808 114.425C685.808 116.121 686.496 117.321 687.84 117.833L688.4 116.361ZM690.24 112.553C691.136 112.697 691.872 113.321 691.872 114.377C691.872 115.465 691.12 116.041 690.24 116.089L690.24 112.553ZM691.696 120.044L686 120.044L686 121.948L691.696 121.948L691.696 123.836L693.056 123.836L693.056 121.948L693.424 121.948C694.416 121.948 694.896 122.476 694.896 123.244C694.896 124.028 694.448 124.412 693.984 124.572L694.832 125.66C695.68 125.292 696.272 124.428 696.272 123.068C696.272 121.308 695.2 120.044 693.488 120.044L693.056 120.044L693.056 118.844L691.696 118.844L691.696 120.044ZM693.056 124.797L691.696 124.797L691.696 126.173L688.4 126.173C686.624 126.173 685.808 127.357 685.808 128.813C685.808 130.125 686.544 131.053 687.312 131.485L688.496 130.541C687.76 130.141 687.36 129.853 687.36 129.101C687.36 128.477 687.776 128.029 688.56 128.029L691.696 128.029L691.696 130.797L693.056 130.797L693.056 128.029L695.312 128.029L694.88 126.173L693.056 126.173L693.056 124.797Z'
                }
                fill={'#7E7C78'}
                className={verticalLayout ? classes.flipElement : undefined}
            />
            {AllToothNumbers.map(number => (
                <LinearTooth key={number} number={number} verticalLayout={verticalLayout} />
            ))}
            {AllInterproximalSpaces.map(space => {
                const itemsForSpace = interproximalSpaceItems.filter(i => i.space === space);
                return (
                    <LinearInterproximalSpace
                        key={`${space.tooth1}-${space.tooth2}`}
                        space={space}
                        hasItem={itemsForSpace.length > 0}
                        activeColor={itemsForSpace.find(i => i.activeColor)?.activeColor}
                        verticalLayout={verticalLayout}
                        Action={InterproximalSpaceAction}
                    />
                );
            })}
        </svg>
    );
};
